/**
 * ForgotPasswordPage
 * CCH Care — Feature #11
 * Created: 2026-04-21
 *
 * Public page. User enters email, submits, sees a generic confirmation
 * regardless of whether the email matches a real account (enumeration
 * prevention — matches backend behavior).
 *
 * Uses auth palette tokens — stays navy in both themes.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { requestPasswordReset } from '../../api/auth';
import { COLORS } from '../../config/constants';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]       = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await requestPasswordReset(email.trim());
      // Always show the generic confirmation — do not branch on whether
      // the email actually mapped to an account.
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={brandRowStyle}>
          <div style={brandSquareStyle}>🏥</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.authText, letterSpacing: '-0.02em' }}>
            CCH Care
          </div>
        </div>

        {!submitted ? (
          <>
            <h1 style={headingStyle}>Reset your password</h1>
            <p style={subtitleStyle}>
              Enter the email address associated with your account. We&apos;ll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label htmlFor="forgot-email" style={labelStyle}>Email address</label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@facility.com"
                  autoComplete="email"
                  required
                  style={inputStyle}
                />
              </div>

              {error && (
                <div style={errorBannerStyle}>
                  {error}
                </div>
              )}

              <button type="submit" disabled={submitting} style={{
                ...primaryButtonStyle,
                cursor: submitting ? 'wait' : 'pointer',
                opacity: submitting ? 0.7 : 1,
              }}>
                {submitting ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          </>
        ) : (
          <>
            <div style={{ fontSize: 48, textAlign: 'center', marginBottom: 12 }}>✉️</div>
            <h1 style={{ ...headingStyle, textAlign: 'center' }}>Check your email</h1>
            <p style={{ ...subtitleStyle, textAlign: 'center' }}>
              If an account exists for <strong style={{ color: COLORS.authText }}>{email}</strong>,
              we&apos;ve sent a password reset link. The link expires in 15 minutes.
            </p>
            <p style={{ ...subtitleStyle, textAlign: 'center', fontSize: 12 }}>
              Didn&apos;t receive it? Check your spam folder, or {' '}
              <button
                type="button"
                onClick={() => { setSubmitted(false); setEmail(''); }}
                style={linkButtonStyle}
              >
                try a different email
              </button>.
            </p>
            <button onClick={() => navigate('/login')} style={{ ...primaryButtonStyle, marginTop: 20 }}>
              Return to sign in
            </button>
          </>
        )}

        {!submitted && (
          <div style={footerStyle}>
            Remembered it?{' '}
            <Link to="/login" style={footerLinkStyle}>
              Sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────
const pageStyle = {
  minHeight: '100vh',
  background: COLORS.authBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

const cardStyle = {
  width: '100%',
  maxWidth: 420,
  background: COLORS.authSurface,
  border: `1px solid ${COLORS.authSurfaceBorder}`,
  borderRadius: 14,
  padding: '32px 28px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
};

const brandRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 28,
};

const brandSquareStyle = {
  width: 36,
  height: 36,
  borderRadius: 8,
  background: COLORS.authBrandBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
};

const headingStyle = {
  fontSize: 22,
  fontWeight: 700,
  color: COLORS.authText,
  margin: '0 0 8px',
  letterSpacing: '-0.01em',
};

const subtitleStyle = {
  fontSize: 14,
  color: COLORS.authTextMuted,
  lineHeight: 1.5,
  margin: '0 0 20px',
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: COLORS.authTextLabel,
  display: 'block',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  borderRadius: 8,
  border: `1px solid ${COLORS.authInputBorder}`,
  background: COLORS.authInputBg,
  color: COLORS.authText,
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  outline: 'none',
};

const primaryButtonStyle = {
  width: '100%',
  padding: '12px 20px',
  borderRadius: 8,
  border: 'none',
  background: COLORS.authPrimary,
  color: '#fff',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const linkButtonStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: COLORS.authLink,
  fontSize: 'inherit',
  fontFamily: 'inherit',
  cursor: 'pointer',
  textDecoration: 'underline',
};

const errorBannerStyle = {
  padding: '10px 14px',
  borderRadius: 8,
  background: COLORS.authErrorBg,
  border: `1px solid ${COLORS.authErrorBorder}`,
  color: COLORS.authErrorText,
  fontSize: 13,
};

const footerStyle = {
  marginTop: 20,
  textAlign: 'center',
  fontSize: 13,
  color: COLORS.authTextHelper,
};

const footerLinkStyle = {
  color: COLORS.authLink,
  textDecoration: 'none',
  fontWeight: 600,
};